import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Box, Typography, FormControl, Select, MenuItem, TextField, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Switch, styled } from '@mui/material';
import axios from 'axios'; 
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'green',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: 'red',
    opacity: 1,
  },
}));

const MyTable = () => {
  const [data, setData] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);  // State for Dialog
  const [deleteId, setDeleteId] = useState(null);  // State to store the item to be deleted

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/itemattribute/getall`) 
      .then(response => {
        const fetchedData = response.data.attributes.map(item => ({
          id: item._id,
          itemName: item.name,
        }));
        setData(fetchedData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  }, []);
  
  const navigate = useNavigate();

  const columns = [
    {
      name: 'Name',
      selector: row => row.itemName,
      sortable: true,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Actions',
      cell: row => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Edit
            sx={{
              cursor: 'pointer',
              backgroundColor: 'green',
              color: '#fff',
              borderRadius: '50%',
              padding: '5px',
              fontSize: '28px',
            }}
            onClick={() => navigate(`edit/${row.id}`)}
          />
          <Delete
            sx={{
              cursor: 'pointer',
              backgroundColor: 'red',
              color: '#fff',
              borderRadius: '50%',
              padding: '6px',
              fontSize: '28px',
            }}
            onClick={() => handleDeleteClick(row.id)} 
          />
        </Box>
      ),
      sortable: false,
      style: {
        padding: '8px',
      },
    },
  ];

  const handleDeleteClick = (id) => {
    setDeleteId(id);  
    setOpenDialog(true);  
  };

  const handleDeleteConfirm = () => {
    axios.delete(`${process.env.REACT_APP_API_URL}/itemattribute/delete/${deleteId}`)
      .then(response => {
        setData(data.filter(item => item.id !== deleteId));  
        setOpenDialog(false); 
        toast.success('Item deleted successfully');
      })
      .catch(error => {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item');
        setOpenDialog(false); 
      });
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);  
  };

  const filteredData = data.filter(item =>
    item.id.toString().includes(searchTerm) ||
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (page - 1) * entriesPerPage;
  const currentPageData = filteredData.slice(startIndex, startIndex + entriesPerPage);

  const handleEntriesChange = event => {
    setEntriesPerPage(event.target.value);
    setPage(1); 
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' ,flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Show
          </Typography>
          <FormControl sx={{ p: 1 }}>
            <Select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Entries per page' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>25</MenuItem>
              <MenuItem value={25}>50</MenuItem>
              <MenuItem value={30}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body1" sx={{ mr: 1 }}>
            entries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Search:
          </Typography>
          <TextField
            className="recent-or-search-cls"
            id="outlined-basic"
            label=""
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>
      </Box>  
      <Box sx={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ marginLeft: 2 }}>Item Attribute loading...</Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={currentPageData}
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            onChangePage={newPage => setPage(newPage)}
            paginationPerPage={entriesPerPage}
            paginationComponentOptions={{
              noRowsPerPage: true,
            }}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                },
              },
              cells: {
                style: {
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  color: 'green',
                  fontSize: '14px',
                },
              },
              headCells: {
                style: {
                  backgroundColor: '#f4f4f4',
                  fontWeight: 'bold',
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  fontSize: '1rem',
                },
              },
              rows: {
                style: {
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                  '&:nth-child(odd)': {
                    boxShadow: 'inset 0 0 0 9999px rgba(0, 0, 0, 0.023)',
                  },
                },
              },
            }}
          />
        )}
      </Box>
      <Dialog open={openDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to delete this Item attribute?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MyTable;
